import React from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import ContainerPageLayout, { CodeBlock, ReqBadge, DownloadButtons } from '../components/ContainerPageLayout';
import ApiPlayground from '../components/ApiPlayground';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'making-request', label: 'Making The Request', children: [
    { id: 'sample-request-url', label: 'Sample Request URL' },
    { id: 'sample-csharp', label: 'Sample 3D Secure Confirm Request' },
    { id: 'request-reference', label: 'Request Reference' },
  ]},
  { id: 'available-form-fields', label: 'Available Form Fields' },
  { id: 'validation', label: 'Field Validation' },
  { id: 'sample-xml', label: 'Sample 3D Secure Confirm XML' },
  { id: 'data-returned', label: 'Data Returned' },
];

const REQUEST_URL_SAMPLE = `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=confirm&Data=<R>...</R>`;

const CSHARP_SAMPLE = `// Location: /TermURL/
[HttpPost]
public IActionResult index(string PaRes)
{
    HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx"); //Create webrequest while setting the API URL to eComm staging environment
    UTF8Encoding encoding = new UTF8Encoding(); //Represents a UTF-8 encoding of Unicode characters.

    string pData = "username=SomeName"; //API Username
    pData += "&password=SomePassword"; //API Password
    pData += "&messageId=" + System.Guid.NewGuid().ToString();  //A new GUID is required for every new API Call
    pData += "&ApiSignature=Confirm"; //API Signature - Please notice the \`Confirm\` signature here for the 3D Secure Confirm request

    pData += "&Data=" + WebUtility.UrlEncode(@"<R><R1>" + PaRes + @"</R1>...</R>"); //Data fields required for request

    byte[] data = encoding.GetBytes(pData); //Getting Bytes for data

    httpWReq.Method = "POST"; //HTTP Method - POST
    httpWReq.ContentType = "application/x-www-form-urlencoded"; //Setting the correct Content Type
    httpWReq.ContentLength = data.Length; //Getting Content Length

    //Create POST data and convert it to a byte array.
    byte[] byteArray = Encoding.UTF8.GetBytes(pData);

    // Get the request stream.
    Stream dataStream = httpWReq.GetRequestStream();

    // Write the data to the request stream.
    dataStream.Write(byteArray, 0, byteArray.Length);

    // Close the Stream object.
    dataStream.Close();

    HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse(); //Assign response to \`response\` variable
    string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd(); //Obtain response from post

    /* Parse XML from response */
    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(responseString);
    /* END */

    /* Obtain information from response fields */
    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
    //... etc

    //Or loop through the elements
    /* END */

    //More code...

    return View();
}`;

const CONFIRM_XML = `<!-- 3D Secure Confirm Request Demonstration -->
<R>
    <R1>PaRes</R1>
    <R2>43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R2>
    <R3>1000596</R3>
    <R4>hrETn5Lu6UxoZry2VUJ1</R4>
</R>`;

const FORM_FIELDS = [
  { name: 'R1', desc: 'PaRes generated by the external system that processed the authentication with the Consumer.', req: 'Y', def: 'AN(10240)' },
  { name: 'R2', desc: 'Transaction Code. The Transaction Code used in the Sale request.', req: 'Y', def: 'AN(40)' },
  { name: 'R3', desc: 'Vendor ID.', req: 'Y', def: 'AN(50)' },
  { name: 'R4', desc: 'Cardinal Transaction ID.', req: 'Y', def: 'AN(20)' },
];

const VALIDATION = [
  { name: 'R1', desc: 'PaRes.', regex: 'N/A' },
  { name: 'R2', desc: 'Transaction Code.', regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R3', desc: 'Vendor ID.', regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R4', desc: 'Cardinal Transaction ID.', regex: "^[a-zA-Z0-9 ,'._-]{0,20}$" },
];

const RETURN_FIELDS = [
  { name: 'R1', desc: (<>Error Code. Please see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link> for a list of available error codes.</>), def: 'N(4)' },
  { name: 'R2', desc: 'Description — human-readable message paired with R1.', def: 'AN(250)' },
];

const ThreeDSecureConfirm = () => (
  <ContainerPageLayout
    currentId="3d-secure-confirm"
    title="3D Secure Confirm"
    titleBadge="3DS v2"
    signature="confirm"
    subtitle="Confirms that the cardholder has passed authentication — called from your TermURL to finalise the transaction."
    toc={TOC}
    mainTestId="threeds-confirm-page"
    sidebarTestId="threeds-confirm-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <div className="space-y-4 text-gray-700 text-base leading-relaxed">
        <p>
          Before reading this section, please ensure you have read our <Link to="/technical-documentation/api-reference/3d-secure-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">3D Secure Auth</Link> documentation — that section explains what is required and lists each step in great depth to allow merchants to fully understand how to successfully integrate using 3D Secure.
        </p>
        <p>
          The 3D Secure Confirm API is required to ensure the consumer has passed authentication. You must make the 3D Secure Confirm request <strong>inside your TermURL</strong> as this is where the consumer will be redirected after they input their unique secure code.
        </p>
        <p>
          When redirecting, 3D Secure will <strong>POST the PaRes to your TermURL</strong> where you must obtain and supply this value for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R1&gt;</code> field of the 3D Secure Confirm request. The PaRes is the reply received from the Issuing Bank after the cardholder has been authenticated.
        </p>
        <p>
          The merchant must then provide the <strong>Transaction Code</strong> generated when making the Sale request and supply this value for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R2&gt;</code> field; the <strong>Vendor ID</strong> is then required for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R3&gt;</code> field within the XML document. Value for <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">&lt;R4&gt;</code> should contain the Cardinal Transaction ID present in the initial Sale response.
        </p>
      </div>

      {/* Quick value-map — one-glance summary of the 4 required fields */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { tag: 'R1', label: 'PaRes', source: 'POSTed to your TermURL' },
          { tag: 'R2', label: 'Transaction Code', source: 'Your original Sale request' },
          { tag: 'R3', label: 'Vendor ID', source: 'Issued by eCOMM' },
          { tag: 'R4', label: 'Cardinal Transaction ID', source: 'R7 from Sale response' },
        ].map((f) => (
          <div key={f.tag} className="rounded-lg border border-gray-200 bg-gradient-to-br from-white to-blue-50/40 p-4">
            <div className="inline-block px-2 py-0.5 bg-[#1F4E79] text-white text-xs font-mono font-bold rounded mb-2">{f.tag}</div>
            <div className="text-sm font-bold text-gray-900 mb-1">{f.label}</div>
            <div className="text-xs text-gray-500 leading-snug">{f.source}</div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-4 mt-5 flex items-start gap-3">
        <Info size={18} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Tip:</strong> In a frictionless EMV 3DS 2.x flow, the Issuer may approve the transaction without challenging the cardholder. Even so, the Confirm request can still be called — it will simply be a no-op if authentication wasn't required. Defensive integrations call Confirm whenever <code className="bg-white px-1 rounded">R1=0000</code> is received on Sale.
        </p>
      </div>
    </section>

    {/* Making The Request */}
    <section id="making-request" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Making The Request</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        HTTP is used as the request-response protocol between a merchant site and the eCOMM API. In the back end, a merchant submits a HTTP POST request to the eCOMM server; the server will then return an XML document where the merchant must parse the data and act accordingly. The response contains key information about the request and also contains the requested content.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-3">The request string for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">3D Secure Confirm</code> call must be composed of the following information:</p>
      <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-6">
        <li><strong>Username</strong> = SomeName</li>
        <li><strong>Password</strong> = SomePassword</li>
        <li><strong>MessageID</strong> = <em>*GUID (e.g. 30dd879c-ee2f-11db-8314-0800200c9a66)</em></li>
        <li><strong>APISignature</strong> = confirm</li>
        <li><strong>Data</strong> = Form data in XML format (<a href="#sample-xml" className="text-[#1F4E79] underline">see sample here</a>)</li>
      </ol>

      <div id="sample-request-url" className="scroll-mt-28">
        <h3 className="text-lg font-bold text-gray-900 mb-2">A fully formatted HTTP POST request should look like this:</h3>
        <CodeBlock code={REQUEST_URL_SAMPLE} lang="http" testid="request-url-code" />
      </div>

      <div id="sample-csharp" className="scroll-mt-28 mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="text-xl font-bold text-gray-900">Sample 3D Secure Confirm Request</h3>
          <DownloadButtons />
        </div>
        <CodeBlock code={CSHARP_SAMPLE} lang="csharp" testid="sample-csharp-code" />
      </div>

      <div id="request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="Confirm"
          title="Request Reference — 3D Secure Confirm"
          description="Sample XML to confirm a 3D Secure transaction once you have the PaRes."
          defaultData={CONFIRM_XML.replace('<!-- 3D Secure Confirm Request Demonstration -->\n', '')}
        />
      </div>
    </section>

    {/* Form Fields */}
    <section id="available-form-fields" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">3D Secure Confirm</code> request.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="form-fields-table">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-24">Required</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {FORM_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3"><ReqBadge value={f.req} /></td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Validation */}
    <section id="validation" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Available Form Data Fields — Validation</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below is a table containing all the available fields for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter within the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">3D Secure Confirm</code> request, including validation regular expressions. Use these when constructing the merchant's request data.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Validation (Regex)</th>
            </tr>
          </thead>
          <tbody>
            {VALIDATION.map((v, i) => (
              <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
                <td className="p-3">{v.desc}</td>
                <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Sample XML */}
    <section id="sample-xml" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-2xl font-mono">3D Secure Confirm</code> XML Document</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-2">
        The below sample demonstrates what is expected when passing POST data into the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">data</code> parameter.
      </p>
      <p className="text-sm text-gray-600 mb-4">
        When forming the <code className="bg-gray-100 px-1 py-0.5 rounded font-mono">data</code> parameter, please refer to the <a href="#available-form-fields" className="text-[#1F4E79] underline hover:text-[#163a5f]">guidelines above</a>.
      </p>
      <CodeBlock code={CONFIRM_XML} lang="xml" testid="confirm-xml-code" />
    </section>

    {/* Data Returned */}
    <section id="data-returned" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Data Returned</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        Below are the expected XML documents returned from the request. Please have a read of our <Link to="/technical-documentation/api-reference/sale-pre-auth#expected-responses" className="text-[#1F4E79] underline hover:text-[#163a5f]">Expected XML Documents</Link> on the Sale API documentation — you can find more information about all the expected success responses there.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#1F4E79] text-white">
              <th className="text-left p-3 w-20">Field</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3 w-28">Definition</th>
            </tr>
          </thead>
          <tbody>
            {RETURN_FIELDS.map((f, i) => (
              <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="p-3">{f.desc}</td>
                <td className="p-3 font-mono text-xs">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-5">
        <p className="text-sm text-gray-700">
          <strong>Common outcomes:</strong> A successful confirm typically returns <code className="bg-white px-1 py-0.5 rounded font-mono text-[#c32222] font-semibold">0008</code> (Notification Method set to "Notification" — transaction result sent to notification URL) or <code className="bg-white px-1 py-0.5 rounded font-mono text-[#c32222] font-semibold">0044</code> (Successful Transaction returned directly). Any other code represents an error state — see <Link to="/technical-documentation/api-reference/sale-pre-auth#expected-responses" className="text-[#1F4E79] underline hover:text-[#163a5f]">the full error table</Link>.
        </p>
      </div>
    </section>
  </ContainerPageLayout>
);

export default ThreeDSecureConfirm;
